import localStorageService from "app/pages/sales-intelligence/services/localStorageService";
import type { SidebarTrackingSectionName } from "app/pages/sales-intelligence/sub-modules/right-sidebar/components/Sidebar/SidebarContainer";

type Settings = Partial<{
    sandbox: string;
    uiVersion: string;
    isCustomUiVersion: boolean;
}>;

export type IframeMessageEvent =
    | { message: "set sandbox"; sandbox: string }
    | { message: "set uiversion"; uiVersion: string }
    | { message: "reset uiversion" }
    | { message: "close" };

export type ExtensionAppMode = "salesforce" | "pro-sidebar";

const PRODUCTION = "production";

const getIframeHost = (sandbox = PRODUCTION) => {
    if (sandbox === PRODUCTION) {
        return "https://pro.similarweb.com";
    } else if (sandbox === "local") {
        return "https://local-salesextension.sandbox.similarweb.com";
    }

    return `https://${sandbox}-pro.sandbox.similarweb.com`;
};

const isSandboxAvailable = async (sandbox: string) => {
    if (sandbox === PRODUCTION) {
        return true;
    }

    try {
        const host = getIframeHost(sandbox);
        const response = await fetch(`${host}/sales-extension/healthreport/ishealthy`);
        const text = await response.text();
        return text === "Healthy";
    } catch (_) {
        return false;
    }
};

export const getSettings = async (): Promise<Settings> => {
    const settings = localStorageService.getState<Settings>("extensionIframeSettings");

    if (settings) {
        const sandbox = settings?.sandbox || PRODUCTION;
        const isAvailable = await isSandboxAvailable(sandbox);
        if (isAvailable) {
            return settings;
        }
    }

    return {
        sandbox: PRODUCTION,
        uiVersion: undefined,
        isCustomUiVersion: undefined,
    };
};

export const setSetting = <K extends keyof Settings>(key: K, value: Settings[K]) => {
    const settings = localStorageService.getState<Settings>("extensionIframeSettings") ?? {};

    settings[key] = value;
    localStorageService.setState("extensionIframeSettings", settings);
};

export const getIframeUrl = async ({
    domain,
    urlParams,
}: {
    domain?: string;
    urlParams: {
        mode: ExtensionAppMode;
        trackingSectionName?: SidebarTrackingSectionName;
    };
}) => {
    const settings = await getSettings();
    const sandbox = settings?.sandbox || PRODUCTION;
    const iframeHost = getIframeHost(sandbox);

    const searchParams = new URLSearchParams();
    searchParams.append("sandbox", sandbox);

    Object.entries(urlParams).forEach(([key, value]) => {
        searchParams.append(key, value);
    });

    if (domain) {
        searchParams.append("domain", domain);
    }

    // to use custom ui version uncomment this block
    // if (settings?.uiVersion) {
    //     searchParams.append("uiVersion", settings.uiVersion);
    // }

    return `${iframeHost}/sales-extension/?${searchParams.toString()}`;
};
